import { TrainingSplitMode } from './data-model/training-split';
import { SplitGroup, WorkoutSolverConstants } from './workout-solver-interface';

/**
 * Every per-mode knob the workout solver needs, resolved once, before any
 * solving starts. See `design-docs/training-splits.md`.
 *
 * Why this exists: each training split mode changes several unrelated things —
 * how many workouts the rotation has, whether near-duplicate movements are kept
 * apart, two different set caps, which movements may share a day, and how the
 * day scheduler scores a candidate arrangement. Written as conditionals inside
 * the solver those would scatter across a very long file. Resolving them into
 * one plain object keeps them in one readable place, and makes it possible to
 * see by inspection that `Optimal` still gets exactly the values it always had.
 */

/**
 * The label used to divide movements between days.
 *
 * Push/Pull/Legs uses `push` / `pull` / `legs`, which are worked out from each
 * movement's muscles. Upper/Lower uses `upper` / `lower`, which the app already
 * records for every movement. The two sets never mix inside one mode.
 */
export type { SplitGroup };
export type UpperLowerGroup = 'upper' | 'lower';
export type DayGroupId = SplitGroup | UpperLowerGroup;

/** One group of movements, and how many days of the rotation it gets. */
export interface DayGroupPlan {
  group: DayGroupId;
  days: number;
}

export interface SplitPolicy {
  mode: TrainingSplitMode;
  /**
   * The rotation length to start from — how many workouts repeat before the
   * pattern begins again (an A/B/C rotation is 3).
   *
   * It is the STARTING value, not a fixed one: when the requested volume does
   * not fit, the solver may shorten the rotation itself. `pinnedRotation` is
   * what forbids that.
   */
  initialSplitsPerLoop: number;
  /** When true, the solver may not shorten the rotation to make volume fit. */
  pinnedRotation: boolean;
  /**
   * How the rotation's days are divided between groups of movements, or
   * `undefined` when the mode does not divide them (Optimal and Full-body put
   * every movement into one scheduling run, as the app has always done).
   */
  dayGroups?: DayGroupPlan[];
  /**
   * Whether the rule that keeps near-duplicate movements off the same day
   * applies at all. For the modes that schedule per group it is further
   * restricted to groups that got more than one day — a group with a single
   * day cannot satisfy it, because every movement in the group shares that day
   * by construction.
   */
  avoidsEnabled: boolean;
  /** Largest number of sets one workout may hold. */
  perWorkoutSetCap: number;
  /** Smallest number of sets a movement carries when it is used at all. */
  perSlotSetMin: number;
  /** Largest number of sets one movement may carry in one workout. */
  perSlotSetMax: number;
  /**
   * Whether the day scheduler should try to spread each muscle across days.
   * Right for Optimal and Full-body; meaningless inside a group whose whole
   * purpose is to train those muscles together, so off for the split modes.
   */
  useMuscleSpreadScoring: boolean;
  /**
   * Whether every workout must train every major muscle group (Menno's
   * Full-body rule). Only `FullBody` sets this.
   */
  requireEveryMajorMuscleEachWorkout: boolean;
  /** Non-empty when the mode cannot be built for these inputs. */
  error: string;
}

export interface SplitPolicyInput {
  mode: TrainingSplitMode;
  /** Training days per week, 1-7. */
  trainingFrequency: number;
  /** All five main lifts are at novice level. */
  allLevelsNovice: boolean;
  /** All five main lifts are at advanced level. */
  allLevelsAdvanced: boolean;
  constants: WorkoutSolverConstants;
  /**
   * REQUIRED. True when the user trains twice a week on two calendar-adjacent
   * days. That case forces a 2-workout rotation, and a training-days COUNT
   * alone cannot tell you it — so leaving this out silently changes the
   * programs of every such user.
   */
  consecutive2x: boolean;
  /**
   * REQUIRED, and `undefined` outside re-optimisation. When the solver is
   * re-optimising a plan the user already has, the rotation length comes from
   * that plan's own workout count rather than from the frequency table — see
   * #4251. Leaving it out reintroduces a bug that was already found and fixed.
   */
  volumeOnlyWorkoutCount?: number;
}

/**
 * The fewest training days a week each mode can be built for.
 *
 * This lives here, rather than beside the rotation lengths in the server's
 * constants file, because the client needs these numbers too — to grey out an
 * option the user cannot have yet, and to fall back to Optimal when someone
 * lowers their training days below the mode they picked. The client cannot see
 * the server's constants, so a copy there would drift the first time one of
 * these numbers changed.
 */
export const SPLIT_MODE_MIN_FREQUENCY: { [key in TrainingSplitMode]: number } = {
  [TrainingSplitMode.Optimal]: 1,
  [TrainingSplitMode.PushPullLegs]: 3,
  [TrainingSplitMode.UpperLower]: 2,
  [TrainingSplitMode.FullBody]: 1,
};

/** How many training days a week this mode needs before it can be offered. */
export function getMinimumFrequencyForSplitMode(mode: TrainingSplitMode): number {
  return SPLIT_MODE_MIN_FREQUENCY[mode] ?? 1;
}

/**
 * The mode to actually solve with, given what the user picked and how often
 * they train.
 *
 * Below a mode's minimum training frequency the solver refuses to build it, so
 * somebody has to step in before that happens. This is that step: it hands back
 * `Optimal` without touching what the user chose, so raising the training days
 * again brings their choice straight back.
 */
export function resolveEffectiveSplitMode(mode: TrainingSplitMode, trainingFrequency: number): TrainingSplitMode {
  return trainingFrequency >= getMinimumFrequencyForSplitMode(mode) ? mode : TrainingSplitMode.Optimal;
}

/** The groups each mode divides its days between, in the order they are shown. */
const MODE_DAY_GROUPS: { [key in TrainingSplitMode]?: DayGroupId[] } = {
  [TrainingSplitMode.PushPullLegs]: ['push', 'pull', 'legs'],
  [TrainingSplitMode.UpperLower]: ['upper', 'lower'],
};

/** The groups this mode divides its days between, or an empty list. */
export function getDayGroupsForMode(mode: TrainingSplitMode): DayGroupId[] {
  return MODE_DAY_GROUPS[mode] ?? [];
}

/** Rotation length for a mode that pins it, or 0 when the mode cannot be built. */
function pinnedRotationLength(mode: TrainingSplitMode, trainingFrequency: number, constants: WorkoutSolverConstants): number {
  const table = constants.splitModes;
  const byFrequency =
    mode === TrainingSplitMode.PushPullLegs
      ? table?.pushPullLegsRotation
      : mode === TrainingSplitMode.UpperLower
        ? table?.upperLowerRotation
        : undefined;
  if (!byFrequency) {
    return 0;
  }
  return byFrequency[trainingFrequency] || 0;
}

/**
 * Work out every per-mode knob for one solve.
 *
 * `Optimal` returns exactly the values the solver used before this feature
 * existed, which is what makes "existing users are unaffected" checkable by
 * reading this function rather than only by running the tests.
 */
export function resolveSplitPolicy(input: SplitPolicyInput): SplitPolicy {
  const { mode, trainingFrequency, allLevelsNovice, allLevelsAdvanced, constants, consecutive2x, volumeOnlyWorkoutCount } = input;

  const frequencyParams = Object.assign({}, constants.frequencies[0], constants.frequencies[trainingFrequency]);

  // the rotation length the app has always used for this frequency and level
  let splitsPerLoop =
    (allLevelsNovice && frequencyParams.splitsPerLoopNovice) ||
    (allLevelsAdvanced && frequencyParams.splitsPerLoopAdvanced) ||
    frequencyParams.splitsPerLoop;

  // special case for 2x/wk on two calendar-adjacent days
  if (trainingFrequency === 2 && consecutive2x) {
    splitsPerLoop = 2;
  }

  const optimalPolicy: SplitPolicy = {
    mode: TrainingSplitMode.Optimal,
    initialSplitsPerLoop: splitsPerLoop,
    pinnedRotation: false,
    dayGroups: undefined,
    avoidsEnabled: true,
    perWorkoutSetCap: frequencyParams.workoutSetsMax,
    perSlotSetMin: frequencyParams.exerciseSetsDailyMin,
    perSlotSetMax: frequencyParams.exerciseSetsDailyMax,
    useMuscleSpreadScoring: true,
    requireEveryMajorMuscleEachWorkout: false,
    error: '',
  };

  // Re-optimising a plan the user already has: the workouts and which day each
  // movement sits on are already decided, so none of the mode's day rules can
  // apply. Only the rotation length changes, and it comes from that plan (#4251).
  if (volumeOnlyWorkoutCount) {
    return { ...optimalPolicy, initialSplitsPerLoop: volumeOnlyWorkoutCount };
  }

  if (mode === TrainingSplitMode.Optimal) {
    return optimalPolicy;
  }

  const minimumFrequency = getMinimumFrequencyForSplitMode(mode);
  if (trainingFrequency < minimumFrequency) {
    return {
      ...optimalPolicy,
      mode,
      error: `The ${mode} training split needs at least ${minimumFrequency} training days a week.`,
    };
  }

  if (mode === TrainingSplitMode.FullBody) {
    // Full-body keeps the ordinary rotation and one single scheduling run; what
    // changes is the requirement that each workout covers every major muscle.
    return { ...optimalPolicy, mode, requireEveryMajorMuscleEachWorkout: true };
  }

  const groups = getDayGroupsForMode(mode);
  const rotation = pinnedRotationLength(mode, trainingFrequency, constants);
  if (!groups.length || !rotation) {
    return {
      ...optimalPolicy,
      mode,
      error: `No rotation is defined for the ${mode} training split at ${trainingFrequency} training days a week.`,
    };
  }
  if (rotation % groups.length !== 0) {
    return {
      ...optimalPolicy,
      mode,
      error: `The ${mode} rotation of ${rotation} days cannot be divided evenly between ${groups.length} groups of movements.`,
    };
  }

  const daysPerGroup = rotation / groups.length;
  return {
    mode,
    initialSplitsPerLoop: rotation,
    pinnedRotation: true,
    dayGroups: groups.map(group => ({ group, days: daysPerGroup })),
    // A group that gets a single day cannot keep two of its own movements
    // apart, so the rule only means anything where a group got two days.
    avoidsEnabled: daysPerGroup > 1,
    perWorkoutSetCap: constants.splitModes?.workoutSetsMax ?? frequencyParams.workoutSetsMax,
    perSlotSetMin: frequencyParams.exerciseSetsDailyMin,
    perSlotSetMax: constants.splitModes?.exerciseSetsDailyMax ?? frequencyParams.exerciseSetsDailyMax,
    useMuscleSpreadScoring: false,
    requireEveryMajorMuscleEachWorkout: false,
    error: '',
  };
}
